import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';
import { 
  AlertTriangle, 
  CheckCircle, 
  Flag, 
  RefreshCw, 
  FileText, 
  CreditCard, 
  Calendar,
  Search,
  Filter,
  Eye,
  Clock,
  User,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = BACKEND_URL;

const CEOAudit = () => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState({
    status: 'pending',
    audit_type: null
  });
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ceo-audit/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.audit_type) params.append('audit_type', filter.audit_type);
      
      const response = await axios.get(`${API}/api/ceo-audit/items?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchSummary();
    fetchItems();
  }, [fetchSummary, fetchItems]);

  const runScan = async () => {
    try {
      setScanning(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ceo-audit/scan-backdated?days_back=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Scan complete!\n\nBackdated Bills: ${response.data.results.backdated_bills}\nDay Closing Edits: ${response.data.results.dayclosing_edits}\nBackdated Bank: ${response.data.results.backdated_bank}`);
      fetchSummary();
      fetchItems();
    } catch (error) {
      console.error('Scan failed:', error);
      alert('Scan failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScanning(false);
    }
  };

  const reviewItem = async (itemId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/ceo-audit/${itemId}/review?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSummary();
      fetchItems();
    } catch (error) {
      console.error('Review failed:', error);
      alert('Review failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const reviewSelected = async (status) => {
    if (selectedItems.size === 0) {
      alert('No items selected');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/ceo-audit/review-bulk?status=${status}`, 
        Array.from(selectedItems),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedItems(new Set());
      fetchSummary();
      fetchItems();
    } catch (error) {
      console.error('Bulk review failed:', error);
      alert('Bulk review failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const toggleSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(i => i.id)));
    }
  };

  const getTypeIcon = (auditType) => {
    switch (auditType) {
      case 'backdated_bill':
        return <FileText className="w-5 h-5 text-orange-500" />;
      case 'backdated_bank':
        return <CreditCard className="w-5 h-5 text-blue-500" />;
      case 'dayclosing_edit':
        return <Calendar className="w-5 h-5 text-purple-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (auditType) => {
    switch (auditType) {
      case 'backdated_bill':
        return 'Backdated Bill';
      case 'backdated_bank':
        return 'Backdated Bank Entry';
      case 'dayclosing_edit':
        return 'Day Closing Edit';
      default:
        return auditType;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>;
      case 'reviewed':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Reviewed</span>;
      case 'flagged':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Flagged</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ceo-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CEO Audit</h1>
          <p className="text-sm text-gray-500 mt-1">Review backdated entries and suspicious activities</p>
        </div>
        <button
          onClick={runScan}
          disabled={scanning}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          data-testid="scan-btn"
        >
          <Search className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning...' : 'Scan for Backdated Entries'}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div 
            className={`p-4 rounded-lg border cursor-pointer transition-all ${filter.status === 'pending' ? 'bg-yellow-50 border-yellow-300' : 'bg-white border-gray-200 hover:border-yellow-300'}`}
            onClick={() => setFilter({ ...filter, status: 'pending', audit_type: null })}
            data-testid="pending-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">{summary.total_pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          
          <div 
            className={`p-4 rounded-lg border cursor-pointer transition-all ${filter.audit_type === 'backdated_bill' ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200 hover:border-orange-300'}`}
            onClick={() => setFilter({ status: 'pending', audit_type: 'backdated_bill' })}
            data-testid="bills-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Backdated Bills</p>
                <p className="text-2xl font-bold text-orange-600">{summary.by_type.backdated_bills}</p>
              </div>
              <FileText className="w-8 h-8 text-orange-400" />
            </div>
          </div>
          
          <div 
            className={`p-4 rounded-lg border cursor-pointer transition-all ${filter.audit_type === 'dayclosing_edit' ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:border-purple-300'}`}
            onClick={() => setFilter({ status: 'pending', audit_type: 'dayclosing_edit' })}
            data-testid="dayclosing-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Day Closing Edits</p>
                <p className="text-2xl font-bold text-purple-600">{summary.by_type.dayclosing_edits}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-400" />
            </div>
          </div>
          
          <div 
            className={`p-4 rounded-lg border cursor-pointer transition-all ${filter.audit_type === 'backdated_bank' ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-300'}`}
            onClick={() => setFilter({ status: 'pending', audit_type: 'backdated_bank' })}
            data-testid="bank-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Backdated Bank</p>
                <p className="text-2xl font-bold text-blue-600">{summary.by_type.backdated_bank}</p>
              </div>
              <CreditCard className="w-8 h-8 text-blue-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
        <Filter className="w-4 h-4 text-gray-500" />
        <select
          value={filter.status || ''}
          onChange={(e) => setFilter({ ...filter, status: e.target.value || null })}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="status-filter"
        >
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="flagged">Flagged</option>
          <option value="">All Status</option>
        </select>
        
        <select
          value={filter.audit_type || ''}
          onChange={(e) => setFilter({ ...filter, audit_type: e.target.value || null })}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="type-filter"
        >
          <option value="">All Types</option>
          <option value="backdated_bill">Backdated Bills</option>
          <option value="dayclosing_edit">Day Closing Edits</option>
          <option value="backdated_bank">Backdated Bank</option>
        </select>

        {filter.status === 'pending' && selectedItems.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-600">{selectedItems.size} selected</span>
            <button
              onClick={() => reviewSelected('reviewed')}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
              data-testid="bulk-review-btn"
            >
              <CheckCircle className="w-4 h-4" /> Mark Reviewed
            </button>
            <button
              onClick={() => reviewSelected('flagged')}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1"
              data-testid="bulk-flag-btn"
            >
              <Flag className="w-4 h-4" /> Flag
            </button>
          </div>
        )}
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-gray-600">No audit items found</p>
          <p className="text-sm text-gray-400 mt-1">Run a scan to check for backdated entries</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Header Row */}
          {filter.status === 'pending' && (
            <div className="flex items-center px-4 py-2 bg-gray-50 border-b border-gray-200">
              <input
                type="checkbox"
                checked={selectedItems.size === items.length && items.length > 0}
                onChange={selectAll}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-500">Select All</span>
            </div>
          )}
          
          {items.map((item) => (
            <div key={item.id} className="border-b border-gray-100 last:border-0">
              <div 
                className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleExpand(item.id)}
              >
                {filter.status === 'pending' && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelect(item.id);
                    }}
                    className="w-4 h-4 rounded border-gray-300 mr-3"
                  />
                )}
                
                <div className="mr-3">
                  {getTypeIcon(item.audit_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400">{getTypeLabel(item.audit_type)}</span>
                    {getStatusBadge(item.status)}
                  </div>
                  <p className="text-sm text-gray-900 mt-0.5 truncate">{item.description}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" /> {item.created_by_name}
                    </span>
                    <span>Entry: {item.entry_date}</span>
                    <span>Created: {item.created_date}</span>
                  </div>
                </div>
                
                {item.status === 'pending' && (
                  <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => reviewItem(item.id, 'reviewed')}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                      title="Mark as Reviewed"
                      data-testid={`review-btn-${item.id}`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => reviewItem(item.id, 'flagged')}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Flag for Investigation"
                      data-testid={`flag-btn-${item.id}`}
                    >
                      <Flag className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {expandedItems.has(item.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                )}
              </div>
              
              {/* Expanded Details */}
              {expandedItems.has(item.id) && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(item.details || {}).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-900">
                          {typeof value === 'number' ? `₹${value.toLocaleString()}` : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {item.reviewed_by_name && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        Reviewed by {item.reviewed_by_name} on {new Date(item.reviewed_at).toLocaleDateString()}
                        {item.review_notes && ` - "${item.review_notes}"`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CEOAudit;
